<template>
  <div class="profile-content">
    <!-- 데스크톱: 회원 관리 -->
    <div class="member-card desktop-layout">
      <h2 class="card-title">회원 정보</h2>

      <div class="member-content">
        <div class="member-layout">
          <div class="logo-container">
            <img
              class="logo-img"
              :src="brandMark"
              alt="brand mark"
              loading="lazy"
            />
          </div>

          <div class="info-grid">
            <div class="info-column">
              <div class="info-item">
                <span class="label">이름</span>
                <span class="value">{{ profile.name }}</span>
              </div>
              <div class="info-item">
                <span class="label">전화번호</span>
                <span class="value">{{ formattedPhoneNumber }}</span>
              </div>
              <div class="info-item">
                <span class="label">비밀번호</span>
                <button
                  type="button"
                  class="change-btn"
                  @click="goToForgotPassword"
                >
                  비밀번호 변경
                </button>
              </div>
            </div>

            <div class="info-column">
              <div class="info-item">
                <span class="label">수강권</span>
                <span class="value">{{ profile.membership }}</span>
              </div>
              <div class="info-item">
                <span class="label">가입일</span>
                <span class="value">{{ profile.join_date }}</span>
              </div>
              <div class="info-item">
                <span class="label">남은 수업 횟수</span>
                <span class="value">{{ profile.remaining }}</span>
              </div>
            </div>

            <div class="info-column">
              <div class="info-item">
                <span class="label">수강권 일자</span>
                <span class="value">{{ profile.period }}</span>
              </div>
              <div class="info-item">
                <span class="label">출석</span>
                <span class="value">{{ profile.attendance }}</span>
              </div>
              <div class="info-item">
                <span class="label">불참</span>
                <span class="value">{{ profile.absence }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 모바일: 개인정보 -->
    <div class="member-card mobile-layout personal-info">
      <h2 class="card-title">개인정보</h2>
      <div class="mobile-personal-content">
        <div class="mobile-logo-container">
          <img
            class="mobile-logo-img"
            :src="brandMark"
            alt="brand mark"
            loading="lazy"
          />
        </div>
        <div class="mobile-personal-info-row">
          <div class="info-item">
            <span class="label">이름</span>
            <span class="value">{{ profile.name }}</span>
          </div>
          <div class="info-item">
            <span class="label">전화번호</span>
            <span class="value">{{ formattedPhoneNumber }}</span>
          </div>
        </div>
        <div class="info-item mobile-full-width">
          <span class="label">비밀번호</span>
          <button type="button" class="change-btn" @click="goToForgotPassword">
            비밀번호 변경
          </button>
        </div>
      </div>
    </div>

    <!-- 모바일: 수강권 정보 -->
    <div class="member-card mobile-layout membership-info">
      <h2 class="card-title">수강권 정보</h2>
      <div class="mobile-membership-content">
        <div class="membership-row">
          <div class="info-item">
            <span class="label">수강권</span>
            <span class="value">{{ profile.membership }}</span>
          </div>
          <div class="info-item">
            <span class="label">가입일</span>
            <span class="value">{{ profile.join_date }}</span>
          </div>
        </div>
        <div class="info-item mobile-full-width">
          <span class="label">수강권 일자</span>
          <span class="value">{{ profile.period }}</span>
        </div>
        <div class="membership-row">
          <div class="info-item">
            <span class="label">총 출석</span>
            <span class="value">{{ profile.attendance }}</span>
          </div>
          <div class="info-item">
            <span class="label">불참</span>
            <span class="value">{{ profile.absence }}</span>
          </div>
        </div>
        <div class="info-item mobile-full-width">
          <span class="label">남은 수업 횟수</span>
          <span class="value danger">{{ profile.remaining }}</span>
        </div>
      </div>
    </div>

    <!-- 출석 내역 -->
    <div class="attendance-card">
      <h2 class="card-title">출석 내역</h2>

      <div class="attendance-list" @scroll="onScroll">
        <div
          v-for="record in attendanceRecords"
          :key="record.id"
          class="attendance-item"
        >
          <div
            class="attendance-indicator"
            :class="record.attended ? 'attended' : 'absent'"
          ></div>
          <div class="attendance-info">
            <div class="attendance-date">
              {{ formatDate(record.date) }} {{ record.time }}
            </div>
            <div class="attendance-type" :class="getTypeClass(record.type)">
              {{ record.type }}
            </div>
          </div>
          <button
            class="attendance-action"
            :class="record.attended ? 'check' : 'absent'"
            @click="record.attended && goToRecords(record.date)"
            :disabled="!record.attended"
          >
            {{ record.attended ? "기록 확인" : "불참처리" }}
          </button>
        </div>

        <!-- 로딩 상태 -->
        <div v-if="loadingAttendance" class="loading-more">
          <p>출석내역을 불러오는 중...</p>
        </div>

        <!-- 더보기 버튼 -->
        <div v-if="hasMoreAttendance && !loadingAttendance" class="load-more">
          <button @click="loadMoreAttendance" class="load-more-btn">
            더보기
          </button>
        </div>

        <!-- 데이터 끝 알림 -->
        <div
          v-if="
            !hasMoreAttendance &&
            attendanceRecords.length > 0 &&
            !loadingAttendance
          "
          class="end-message"
        >
          <p>모든 출석내역을 불러왔습니다.</p>
        </div>

        <!-- 빈 상태 -->
        <div
          v-if="attendanceRecords.length === 0 && !loadingAttendance"
          class="empty-state"
        >
          <p>출석내역이 없습니다.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiJson } from "@/lib/api";
import brandMark from "@/assets/logo/brand-mark.png";

export default {
  name: "ProfileView",
  data() {
    return {
      brandMark,
      profile: {
        name: "-",
        phone_number: "-",
        membership: "-",
        join_date: "-",
        remaining: "-",
        period: "-",
        attendance: 0,
        absence: 0,
      },
      attendanceRecords: [],
      loadingAttendance: false,
      hasMoreAttendance: true,
      attendanceOffset: 0,
    };
  },
  computed: {
    formattedPhoneNumber() {
      if (!this.profile.phone_number || this.profile.phone_number === "-") {
        return this.profile.phone_number;
      }

      // 숫자만 추출
      const phone = this.profile.phone_number.replace(/\D/g, "");

      // 010-XXXX-XXXX 형식으로 포맷팅
      if (phone.length === 11 && phone.startsWith("010")) {
        return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
      }

      // 다른 형식의 전화번호 처리
      if (phone.length === 10) {
        return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
      }

      // 포맷팅할 수 없는 경우 원본 반환
      return this.profile.phone_number;
    },
  },
  created() {
    this.loadProfile();
    this.loadAttendance();
  },
  methods: {
    goToForgotPassword() {
      this.$router.push("/forgot-password");
    },
    async loadProfile() {
      try {
        const data = await apiJson("/v1/me/profile");
        this.profile = { ...this.profile, ...data };
      } catch {
        // mock/개발시에도 UI는 유지
      }
    },
    async loadAttendance() {
      try {
        this.loadingAttendance = true;
        const data = await apiJson(`/v1/me/attendance?offset=0&limit=20`);
        this.attendanceRecords = data.records || [];
        this.hasMoreAttendance = data.has_more || false;
        this.attendanceOffset = 20;
      } catch (error) {
        console.error("Failed to load attendance:", error);
        this.attendanceRecords = [];
        this.hasMoreAttendance = false;
      } finally {
        this.loadingAttendance = false;
      }
    },
    async loadMoreAttendance() {
      if (this.loadingAttendance || !this.hasMoreAttendance) return;

      try {
        this.loadingAttendance = true;
        const data = await apiJson(
          `/v1/me/attendance?offset=${this.attendanceOffset}&limit=20`
        );
        this.attendanceRecords = [
          ...this.attendanceRecords,
          ...(data.records || []),
        ];
        this.hasMoreAttendance = data.has_more || false;
        this.attendanceOffset += 20;
      } catch (error) {
        console.error("Failed to load more attendance:", error);
      } finally {
        this.loadingAttendance = false;
      }
    },
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadMoreAttendance();
      }
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
      const weekday = weekdays[date.getDay()];
      return `${month}월 ${day}일 (${weekday})`;
    },
    goToRecords(dateStr) {
      // RecordsView로 이동하면서 특정 주차 데이터 표시
      const date = new Date(dateStr);
      const weekOffset = this.calculateWeekOffset(date);
      this.$router.push({
        name: "Records",
        query: { week: weekOffset },
      });
    },
    calculateWeekOffset(targetDate) {
      // RecordsView와 동일한 로직으로 주차 계산
      const now = new Date();
      const target = new Date(targetDate);

      // 현재 주의 시작일 (일요일)
      const nowWeekStart = new Date(now);
      nowWeekStart.setDate(now.getDate() - now.getDay());
      nowWeekStart.setHours(0, 0, 0, 0);

      // 대상 날짜의 주 시작일
      const targetWeekStart = new Date(target);
      targetWeekStart.setDate(target.getDate() - target.getDay());
      targetWeekStart.setHours(0, 0, 0, 0);

      // 주차 차이 계산 (밀리초 단위 → 일 단위 → 주 단위)
      const diffTime = nowWeekStart.getTime() - targetWeekStart.getTime();
      const diffDays = diffTime / (24 * 60 * 60 * 1000);
      const diffWeeks = Math.floor(diffDays / 7);

      return diffWeeks;
    },
    getTypeClass(type) {
      return type === "Hybrid" ? "hyb" : type === "Core" ? "core" : "car";
    },
  },
};
</script>

<style scoped>
.profile-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 회원 관리 카드 */
.member-card {
  background: #151718;
  border-radius: 10px;
  padding: 24px;
  box-sizing: border-box;
}

/* 레이아웃 분리: 데스크톱/모바일 */
.desktop-layout {
  display: block;
}

.mobile-layout {
  display: none;
}

.card-title {
  margin: 0 0 24px 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 24px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.member-content {
  display: flex;
  flex-direction: column;
}

.member-layout {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.logo-container {
  width: 72px;
  height: 72px;
  background: #012f00;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.logo-img {
  height: 50px;
  width: 64px;
  max-width: 52px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  column-gap: 80px;
  flex: 1;
}

.info-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #8e8e8e;
}

.value {
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.value.danger {
  color: #ef4444;
}

.change-btn {
  width: 118px;
  height: 36px;
  background: #ffffff;
  border: 0;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
  text-align: center;
  letter-spacing: -0.02em;
  color: #000000;
  cursor: pointer;
  transition: opacity 0.2s;
}

.change-btn:hover {
  opacity: 0.9;
}

/* 출석 내역 카드 */
.attendance-card {
  background: #151718;
  border-radius: 10px;
  padding: 24px;
  box-sizing: border-box;
}

.attendance-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 8px;
}

.attendance-list::-webkit-scrollbar {
  width: 6px;
}

.attendance-list::-webkit-scrollbar-track {
  background: #2a2a2a;
  border-radius: 3px;
}

.attendance-list::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 3px;
}

.attendance-list::-webkit-scrollbar-thumb:hover {
  background: #777;
}

.attendance-item {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 17.5px 24px;
  display: flex;
  align-items: center;
  gap: 21px;
  height: 91px;
  box-sizing: border-box;
}

.attendance-indicator {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
}

.attendance-indicator.attended {
  background: #78ff47;
}

.attendance-indicator.absent {
  background: #ef4444;
}

.attendance-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.attendance-date {
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.attendance-type {
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 140%;
  letter-spacing: -0.02em;
}

.attendance-type.hyb {
  color: #49de80;
}

.attendance-type.core {
  color: #f59e0c;
}

.attendance-type.car {
  color: #ef4444;
}

.attendance-action {
  width: 118px;
  height: 36px;
  background: #ffffff;
  border: 1px solid #333333;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
  text-align: center;
  letter-spacing: -0.02em;
  color: #000000;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.attendance-action:hover {
  opacity: 0.9;
}

/* 무한 스크롤 관련 스타일 */
.loading-more,
.end-message,
.empty-state {
  text-align: center;
  padding: 20px;
  color: #8e8e8e;
  font-size: 14px;
}

.load-more {
  text-align: center;
  padding: 20px;
}

.load-more-btn {
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 12px 24px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-btn:hover {
  background: #444;
  border-color: #666;
}

@media (max-width: 1100px) {
  .member-layout {
    flex-direction: column;
  }

  .info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 720px) {
  /* 레이아웃 전환 */
  .desktop-layout {
    display: none !important;
  }

  .mobile-layout {
    display: block !important;
  }

  .member-layout {
    flex-direction: column;
    gap: 16px;
  }

  .logo-container {
    width: 120px;
    height: 120px;
  }

  .logo-img {
    height: 80px;
    width: auto;
    max-width: 100px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* 모바일 개인정보 섹션 */
  .mobile-personal-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }

  .mobile-logo-container {
    width: 120px;
    height: 120px;
    background: #012f00;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .mobile-logo-img {
    height: 80px;
    width: auto;
    max-width: 100px;
  }

  .mobile-personal-info-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
  }

  .mobile-full-width {
    width: 100%;
  }

  /* 모바일 수강권 정보 섹션 */
  .mobile-membership-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .membership-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .value.danger {
    color: #ef4444;
  }

  /* 첫 번째 컬럼: 이름/전화번호 한 줄, 비밀번호 변경 다음 줄 */
  .info-column:first-child {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .info-column:first-child .info-item:nth-child(3) {
    grid-column: 1 / 3; /* 비밀번호 변경 버튼 전체 너비 */
  }

  /* 2번째 컬럼: 수강권/가입일 한 줄, 수강권 시작일 다음 줄 */
  .info-column:nth-child(2) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .info-column:nth-child(2) .info-item:nth-child(3) {
    grid-column: 1 / 3;
  }

  /* 3번째 컬럼: 남은 횟수 한 줄, 출석/불참 다음 줄 */
  .info-column:nth-child(3) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .info-column:nth-child(3) .info-item:first-child {
    grid-column: 1 / 3;
  }

  .card-title {
    font-size: 20px;
  }

  /* 모바일에서 출석 인디케이터 숨기기 */
  .attendance-indicator {
    display: none;
  }

  /* 불참 버튼 빨간색 스타일 */
  .attendance-action.absent {
    background: #ef4444;
    border-color: #ef4444;
    color: #ffffff;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .attendance-action.absent:hover {
    opacity: 0.6;
  }

  .attendance-action:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* 기록 확인 버튼은 기존 스타일 유지 */
  .attendance-action.check {
    background: #ffffff;
    border: 1px solid #333333;
    color: #000000;
  }
}
</style>
